import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Input,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tag,
  Image,
  Upload,
  Divider,
  List,
  Drawer,
  FloatButton,
  Badge,
  Grid,
  Segmented
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  WarningOutlined,
  BarcodeOutlined,
  StockOutlined,
  FilterOutlined,
  EyeOutlined,
  MoreOutlined
  ,AppstoreOutlined
} from '@ant-design/icons'
import productService from '../../services/productService'
import UnitsSelect from '../../components/UnitsSelect'
import { useProductCategoriesOptions } from '../../hooks/useProductCategories'
import ProductVariantManager from './ProductVariantManager'

const { Title, Text } = Typography
const { Search } = Input
const { Option } = Select
const { useBreakpoint } = Grid

const ProductsPage = () => {
  const { categories, error, refresh } = useProductCategoriesOptions()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [stockModalVisible, setStockModalVisible] = useState(false)
  const [stockProduct, setStockProduct] = useState(null)
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [productView, setProductView] = useState('sku')
  const [form] = Form.useForm()
  const [stockForm] = Form.useForm()

  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Expected Results:
  console.log('Categories loaded:', categories.length > 0) // Should be true
  console.log('Loading state:', loading === false) // Should be true after load
  console.log('No error:', !error) // Should be true

  // Product categories
  // const categories = [
  //   { value: 'medical_equipment', label: 'อุปกรณ์การแพทย์' },
  //   { value: 'diagnostic_devices', label: 'เครื่องมือวินิจฉัย/ตรวจวัด' }, // เช่น เครื่องวัดความดัน, โอซิลโลสโคปทางการแพทย์, EKG
  //   { value: 'surgical_tools', label: 'อุปกรณ์ผ่าตัด' }, // กรรไกร, มีดผ่าตัด, ชุดผ่าตัด
  //   { value: 'consumables', label: 'เวชภัณฑ์สิ้นเปลือง' }, // ถุงมือ, หน้ากาก, สำลี, ไม้พันสำลี
  //   { value: 'rehabilitation', label: 'อุปกรณ์ฟื้นฟู/กายภาพ' }, // ไม้เท้า, รถเข็น, เครื่องดึงคอ
  //   { value: 'lab_supplies', label: 'อุปกรณ์ห้องแล็บ/ทดสอบ' }, // หลอดทดลอง, microtube, pipette
  //   { value: 'hospital_furniture', label: 'เฟอร์นิเจอร์ทางการแพทย์' }, // เตียง, รถเข็นเตียง, โต๊ะเจาะเลือด
  //   { value: 'ppe', label: 'อุปกรณ์ป้องกันส่วนบุคคล (PPE)' },
  //   { value: 'pharma', label: 'เวชภัณฑ์ยา/เคมีภัณฑ์ทางการแพทย์' }, // แอลกอฮอล์, น้ำยาทำความสะอาดแผล
  //   { value: 'other', label: 'อื่นๆ' }
  // ]

  // Unit options - ตอนนี้ใช้ API แทน hardcoded array
  // หน่วยนับจะถูกโหลดจาก /api/units ผ่าน UnitsSelect component

  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const res = await productService.getAllProducts()
      //console.log('products:', res);
      setProducts(res)
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลสินค้าได้')
    } finally {
      setLoading(false)
    }
  }

  const handleAddProduct = () => {
    setEditingProduct(null)
    setModalVisible(true)
    form.resetFields()
  }

  const handleEditProduct = (product) => {
    //console.log('values:', product);
    setEditingProduct(product)
    setModalVisible(true)
    // Map backend field names to frontend field names for editing
    form.setFieldsValue({
      barcode: product.barcode,
      name: product.name,
      description: product.description,
      category: product.category,
      brand: product.brand,
      price: product.price,
      cost: product.cost,
      unit: product.unit,
      stock: product.stock,
      min_stock: product.min_stock,
      image_url: product.image_url || '',
      status: product.is_active ? 'active' : 'inactive'
    })
  }

  const handleDeleteProduct = async (id) => {
    try {
      await productService.deleteProduct(id)
      message.success('ลบสินค้าสำเร็จ')
      fetchProducts()
    } catch (error) {
      message.error('ไม่สามารถลบสินค้าได้')
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()

      console.log('values', values);

      if (editingProduct) {
        await productService.updateProduct(editingProduct.id, values)
        message.success('อัปเดตสินค้าสำเร็จ')
      } else {
        await productService.createProduct(values)
        message.success('เพิ่มสินค้าสำเร็จ')
      }

      setModalVisible(false)
      form.resetFields()
      fetchProducts()
    } catch (error) {
      if (error.errorFields) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('ไม่สามารถบันทึกข้อมูลได้')
      }
    }
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    form.resetFields()
  }

  const handleSearch = async (value) => {
    setSearchTerm(value)
    if (value.trim()) {
      setLoading(true)
      try {
        const data = await productService.searchProducts(value)
        setProducts(data)
      } catch (error) {
        message.error('ไม่สามารถค้นหาสินค้าได้')
      } finally {
        setLoading(false)
      }
    } else {
      fetchProducts()
    }
  }

  const handleCategoryFilter = async (category) => {
    setSelectedCategory(category)
    if (category === 'all') {
      fetchProducts()
    } else {
      setLoading(true)
      try {
        const data = await productService.getProductsByCategory(category)
        setProducts(data)
      } catch (error) {
        message.error('ไม่สามารถกรองสินค้าตามหมวดหมู่ได้')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleStockUpdate = (product) => {
    setStockProduct(product)
    setStockModalVisible(true)
    stockForm.resetFields()
  }

  const handleStockModalOk = async () => {
    try {
      const values = await stockForm.validateFields()

      await productService.updateStock(
        stockProduct.id,
        values.quantity,
        values.type,
        values.reason
      )

      message.success('อัปเดต Stock สำเร็จ')
      setStockModalVisible(false)
      stockForm.resetFields()
      fetchProducts()
    } catch (error) {
      if (error.errorFields) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('ไม่สามารถอัปเดต Stock ได้')
      }
    }
  }

  const handleStockModalCancel = () => {
    setStockModalVisible(false)
    stockForm.resetFields()
  }

  const normalizedProducts = products
  /*

        unit: product.unit || 'ชิ้น',
        image_src: buildUploadUrl(product.image_url || product.image || product.imageUrl || ''),
        category_label: categoryData?.label || product.category || 'ไม่ระบุหมวดหมู่'
      }
    })
  ), [products, categories])
  */

  const formatCurrency = (value) => `฿${Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`

  // Calculate statistics
  const totalProducts = products.length
  const totalValue = products.reduce((sum, product) => sum + ((Number(product.price) || 0) * (Number(product.stock) || 0)), 0)
  const lowStockProducts = products.filter(product => (Number(product.stock) || 0) <= (Number(product.min_stock) || 0)).length

  const columns = [
    /*
    */
    {
      title: 'รูปภาพ',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 80,
      render: (_, record) => (
        record.image_src ? (
          <Image
            width={50}
            height={50}
            src={record.image_src}
            preview={false}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYSFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhQ=="
          />
        ) : (
          <div style={{
            width: 50,
            height: 50,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4
          }}>
            <BarcodeOutlined style={{ color: '#ccc' }} />
          </div>
        )
      )
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 120,
      render: (barcode) => <Tag color="blue">{barcode}</Tag>
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
      width: 200,
      render: (_, record) => <Tag color="green">{record.category_label}</Tag>
    },
    {
      title: 'ราคา',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) => `₿${price?.toLocaleString() || 0}`
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'stock',
      key: 'stock',
      width: 100,
      render: (stock, record) => {
        const isLowStock = stock <= record.min_stock
        return (
          <Tag color={isLowStock ? 'red' : 'green'}>
            {stock} {record.unit}
            {isLowStock && <WarningOutlined style={{ marginLeft: 4 }} />}
          </Tag>
        )
      }
    },
    {
      title: 'สถานะ',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active) => (
        <Tag color={is_active ? 'green' : 'red'}>
          {is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </Tag>
      )
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<StockOutlined />}
            onClick={() => handleStockUpdate(record)}
            size="small"
            title="จัดการ Stock"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditProduct(record)}
            size="small"
            title="แก้ไข"
          />
          <Popconfirm
            title="คุณแน่ใจว่าต้องการลบสินค้านี้?"
            onConfirm={() => handleDeleteProduct(record.id)}
            okText="ใช่"
            cancelText="ไม่"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              title="ลบ"
            />
          </Popconfirm>
        </Space>
      )
    }
  ]

  // Mobile Product Card Component
  const ProductCard = ({ product }) => {
    const isLowStock = product.stock <= product.min_stock

    return (
      <Card
        bordered={false}
        bodyStyle={{ padding: 14 }}
        style={{
          marginBottom: 14,
          borderRadius: 18,
          boxShadow: '0 10px 24px rgba(23, 50, 77, 0.08)',
          background: 'linear-gradient(180deg, #ffffff 0%, #f7fafc 100%)'
        }}
        actions={[
          <Button
            type="text"
            icon={<StockOutlined />}
            onClick={() => handleStockUpdate(product)}
            size="small"
          >
            Stock
          </Button>,
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditProduct(product)}
            size="small"
          >
            แก้ไข
          </Button>,
          <Popconfirm
            title="ลบสินค้านี้?"
            onConfirm={() => handleDeleteProduct(product.id)}
            okText="ใช่"
            cancelText="ไม่"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              ลบ
            </Button>
          </Popconfirm>
        ]}
      >
        <Row gutter={14} align="top" wrap={false}>
          <Col flex="88px" style={{ height: 88, overflow: 'hidden' }}>
            {product.image_src ? (
              <Image
                width={88}
                height={88}
                src={product.image_src}
                preview={false}
                style={{ objectFit: 'cover', borderRadius: 14 }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYSFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhQ=="
              />
            ) : (
              <div style={{
                width: 88,
                height: 88,
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 14
              }}>
                <BarcodeOutlined style={{ color: '#60a5fa', fontSize: 26 }} />
              </div>
            )}
            {product.description && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">รายละเอียด: </Text>
                <Text>{product.description}</Text>
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              <Text type="secondary">ต้นทุน: </Text>
              <Text strong>{formatCurrency(product.cost)}</Text>
            </div>
            {product.description && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">รายละเอียด: </Text>
                <Text>{product.description}</Text>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">ต้นทุน: </Text>
              <Text strong>{formatCurrency(product.cost)}</Text>
            </div>
          </Col>
          <Col flex="auto">
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 16, color: '#17324d', display: 'block', lineHeight: 1.3 }}>{product.name || 'ไม่ระบุชื่อสินค้า'}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{product.brand || 'ไม่ระบุยี่ห้อ'}</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" size="small">{product.barcode || 'ไม่มีบาร์โค้ด'}</Tag>
                <Tag color="green" size="small">{product.category_label}</Tag>
                <Tag color={product.is_active ? 'green' : 'red'} size="small">
                  {product.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                </Tag>
              </div>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">ราคา:</Text><br />
                <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                  ₿{product.price?.toLocaleString() || 0}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">คงเหลือ:</Text><br />
                <Badge
                  status={isLowStock ? 'error' : 'success'}
                  text={
                    <Text strong style={{ color: isLowStock ? '#ff4d4f' : '#52c41a' }}>
                      {product.stock} {product.unit}
                      {isLowStock && <WarningOutlined style={{ marginLeft: 4 }} />}
                    </Text>
                  }
                />
              </Col>
            </Row>

            {product.brand && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">ยี่ห้อ: </Text>
                <Text>{product.brand}</Text>
              </div>
            )}
            {product.description && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">รายละเอียด: </Text>
                <Text>{product.description}</Text>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">ต้นทุน: </Text>
              <Text strong>{formatCurrency(product.cost)}</Text>
            </div>
          </Col>
        </Row>
      </Card>
    )
  }

  // Render Filters Component
  const FiltersComponent = () => (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Search
            placeholder="ค้นหาสินค้า..."
            allowClear
            onSearch={handleSearch}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={24}>
          <Select
            value={selectedCategory}
            onChange={handleCategoryFilter}
            style={{ width: '100%' }}
            placeholder="เลือกหมวดหมู่"
          >
            <Option value="all">ทุกหมวดหมู่</Option>
            {categories.map(category => (
              <Option key={category.value} value={category.value}>
                {category.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
    </>
  )

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <Title level={2}>
        <ShoppingCartOutlined /> จัดการสินค้า
      </Title>

      <Card size="small" style={{ marginBottom: 16, borderRadius: 14 }}>
        <Row align="middle" justify="space-between" gutter={[12, 12]}>
          <Col xs={24} md={16}>
            <Segmented
              block={isMobile}
              size="large"
              value={productView}
              onChange={setProductView}
              options={[
                { value: 'sku', label: 'สินค้า / SKU', icon: <ShoppingCartOutlined /> },
                { value: 'variants', label: 'สินค้าแม่และ Variant', icon: <AppstoreOutlined /> }
              ]}
            />
          </Col>
          {!isMobile && (
            <Col>
              <Text type="secondary">
                {productView === 'sku' ? 'รายการสินค้าทั้งหมดในระบบ' : 'จัดการขนาด หน่วย BOM Serial และประกัน'}
              </Text>
            </Col>
          )}
        </Row>
      </Card>

      {productView === 'variants' && <ProductVariantManager onChanged={fetchProducts} />}

      {productView === 'sku' && <>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Card>
            <Statistic
              title="จำนวนสินค้าทั้งหมด"
              value={totalProducts}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Card>
            <Statistic
              title="มูลค่าสินค้าคงเหลือ"
              value={totalValue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="₿"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="สินค้าใกล้หมด"
              value={lowStockProducts}
              prefix={<WarningOutlined />}
              valueStyle={{ color: lowStockProducts > 0 ? '#ff4d4f' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Desktop View */}
      {!isMobile && (
        <>
          {/* Filters and Controls */}
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={8}>
                <Search
                  placeholder="ค้นหาสินค้า..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryFilter}
                  style={{ width: '100%' }}
                  placeholder="เลือกหมวดหมู่"
                >
                  <Option value="all">ทุกหมวดหมู่</Option>
                  {categories.map(category => (
                    <Option key={category.value} value={category.value}>
                      {category.label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddProduct}
                >
                  เพิ่มสินค้าใหม่
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Products Table */}
          <Card>
            <Table
              columns={columns}
              dataSource={normalizedProducts}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1000 }}
              pagination={{
                pageSize: 100,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} จาก ${total} รายการ`
              }}
            />
          </Card>
        </>
      )}

      {/* Mobile View */}
      {isMobile && (
        <>
          {/* Mobile Filter Bar */}
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col flex={1}>
                <Search
                  placeholder="ค้นหาสินค้า..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFiltersVisible(true)}
                />
              </Col>
            </Row>
          </Card>

          {/* Mobile Product List */}
          <List
            loading={loading}
            dataSource={normalizedProducts}
            renderItem={(product) => (
              <List.Item style={{ padding: 0 }}>
                <ProductCard product={product} />
              </List.Item>
            )}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              simple: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]}/${total}`
            }}
          />

          {/* Mobile Filters Drawer */}
          <Drawer
            title="ตัวกรอง"
            placement="bottom"
            onClose={() => setFiltersVisible(false)}
            open={filtersVisible}
            height="auto"
          >
            <FiltersComponent />
          </Drawer>

          {/* Float Button */}
          <FloatButton
            icon={<PlusOutlined />}
            type="primary"
            style={{ right: 24, bottom: 24 }}
            onClick={handleAddProduct}
          />
        </>
      )}
      </>}

      {/* Add/Edit Product Modal/Drawer */}
      {isMobile ? (
        <Drawer
          title={editingProduct ? 'แก้ไขสินค้า' : 'เพิ่มสินค้าใหม่'}
          placement="right"
          onClose={handleModalCancel}
          open={modalVisible}
          width="100%"
          extra={
            <Space>
              <Button onClick={handleModalCancel}>ยกเลิก</Button>
              <Button type="primary" onClick={handleModalOk}>
                บันทึก
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              status: 'active',
              unit: 'piece',
              category: 'other',
              min_stock_level: 10
            }}
          >
            <Form.Item
              name="barcode"
              label="รหัสสินค้า"
              rules={[{ required: true, message: 'กรุณาใส่รหัสสินค้า' }]}
            >
              <Input placeholder="เช่น PRD001" />
            </Form.Item>

            <Form.Item
              name="name"
              label="ชื่อสินค้า"
              rules={[{ required: true, message: 'กรุณาใส่ชื่อสินค้า' }]}
            >
              <Input placeholder="ชื่อสินค้า" />
            </Form.Item>

            <Form.Item
              name="description"
              label="รายละเอียด"
            >
              <Input.TextArea
                rows={3}
                placeholder="รายละเอียดสินค้า..."
              />
            </Form.Item>

            <Form.Item
              name="category"
              label="หมวดหมู่"
              rules={[{ required: true, message: 'กรุณาเลือกหมวดหมู่' }]}
            >
              <Select placeholder="เลือกหมวดหมู่">
                {categories.map(category => (
                  <Option key={category.value} value={category.value}>
                    {category.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="brand"
              label="ยี่ห้อ"
            >
              <Input placeholder="ยี่ห้อสินค้า" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="price"
                  label="ราคา"
                  rules={[{ required: true, message: 'กรุณาใส่ราคา' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    addonAfter="₿"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="cost"
                  label="ราคาทุน"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    addonAfter="₿"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="unit"
              label="หน่วย"
              rules={[{ required: true, message: 'กรุณาเลือกหน่วย' }]}
            >
              <UnitsSelect
                placeholder="เลือกหน่วยนับ"
                allowClear
                showRefresh
                valueField="unit_name"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="stock"
                  label="จำนวนคงเหลือ"
                  rules={[{ required: false, message: 'กรุณาใส่จำนวนคงเหลือ' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="min_stock"
                  label="จำนวนขั้นต่ำ"
                  rules={[{ required: true, message: 'กรุณาใส่จำนวนขั้นต่ำ' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="10"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="image_url"
              label="URL รูปภาพ"
            >
              <Input
                placeholder="https://example.com/image.jpg"
              />
            </Form.Item>

            <Form.Item
              name="status"
              label="สถานะ"
            >
              <Select>
                <Option value="active">ใช้งาน</Option>
                <Option value="inactive">ไม่ใช้งาน</Option>
              </Select>
            </Form.Item>
          </Form>
        </Drawer>
      ) : (
        <Modal
          title={editingProduct ? 'แก้ไขสินค้า' : 'เพิ่มสินค้าใหม่'}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={600}
          okText="บันทึก"
          cancelText="ยกเลิก"
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              status: 'active',
              unit: 'piece',
              category: 'other',
              min_stock_level: 10
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="barcode"
                  label="รหัสสินค้า"
                  rules={[{ required: true, message: 'กรุณาใส่รหัสสินค้า' }]}
                >
                  <Input placeholder="เช่น PRD001" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="ชื่อสินค้า"
                  rules={[{ required: true, message: 'กรุณาใส่ชื่อสินค้า' }]}
                >
                  <Input placeholder="ชื่อสินค้า" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="description"
              label="รายละเอียด"
            >
              <Input.TextArea
                rows={3}
                placeholder="รายละเอียดสินค้า..."
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="หมวดหมู่"
                  rules={[{ required: true, message: 'กรุณาเลือกหมวดหมู่' }]}
                >
                  <Select placeholder="เลือกหมวดหมู่">
                    {categories.map(category => (
                      <Option key={category.value} value={category.value}>
                        {category.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="brand"
                  label="ยี่ห้อ"
                >
                  <Input placeholder="ยี่ห้อสินค้า" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="price"
                  label="ราคา"
                  rules={[{ required: true, message: 'กรุณาใส่ราคา' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    addonAfter="₿"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="cost"
                  label="ราคาทุน"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    addonAfter="₿"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="unit"
                  label="หน่วย"
                  rules={[{ required: true, message: 'กรุณาเลือกหน่วย' }]}
                >
                  <UnitsSelect
                    placeholder="เลือกหน่วยนับ"
                    allowClear
                    showRefresh
                    valueField="unit_name"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="stock"
                  label="จำนวนคงเหลือ"
                  rules={[{ required: true, message: 'กรุณาใส่จำนวนคงเหลือ' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="min_stock"
                  label="จำนวนขั้นต่ำ"
                  rules={[{ required: true, message: 'กรุณาใส่จำนวนขั้นต่ำ' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="10"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="image_url"
              label="URL รูปภาพ"
            >
              <Input
                placeholder="https://example.com/image.jpg"
              />
            </Form.Item>

            <Form.Item
              name="status"
              label="สถานะ"
            >
              <Select>
                <Option value="active">ใช้งาน</Option>
                <Option value="inactive">ไม่ใช้งาน</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Stock Management Modal/Drawer */}
      {isMobile ? (
        <Drawer
          title={`จัดการ Stock - ${stockProduct?.name}`}
          placement="right"
          onClose={handleStockModalCancel}
          open={stockModalVisible}
          width="100%"
          extra={
            <Space>
              <Button onClick={handleStockModalCancel}>ยกเลิก</Button>
              <Button type="primary" onClick={handleStockModalOk}>
                อัปเดต
              </Button>
            </Space>
          }
        >
          {stockProduct && (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Stock ปัจจุบัน:</Text><br />
                    <Text style={{ fontSize: 16 }}>{stockProduct.stock} {stockProduct.unit}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>Stock ขั้นต่ำ:</Text><br />
                    <Text style={{ fontSize: 16 }}>{stockProduct.min_stock} {stockProduct.unit}</Text>
                  </Col>
                </Row>
              </Card>

              <Form
                form={stockForm}
                layout="vertical"
                initialValues={{
                  type: 'add'
                }}
              >
                <Form.Item
                  name="type"
                  label="ประเภทการอัปเดต"
                  rules={[{ required: true, message: 'กรุณาเลือกประเภท' }]}
                >
                  <Select>
                    <Option value="add">เพิ่ม Stock</Option>
                    <Option value="subtract">ลด Stock</Option>
                    <Option value="set">กำหนด Stock ใหม่</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="quantity"
                  label="จำนวน"
                  rules={[
                    { required: true, message: 'กรุณาใส่จำนวน' },
                    { type: 'number', min: 0, message: 'จำนวนต้องเป็นตัวเลขบวก' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="จำนวน"
                    addonAfter={stockProduct.unit}
                  />
                </Form.Item>

                <Form.Item
                  name="reason"
                  label="เหตุผล (ไม่บังคับ)"
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="เหตุผลในการอัปเดต stock..."
                  />
                </Form.Item>
              </Form>
            </>
          )}
        </Drawer>
      ) : (
        <Modal
          title={`จัดการ Stock - ${stockProduct?.name}`}
          open={stockModalVisible}
          onOk={handleStockModalOk}
          onCancel={handleStockModalCancel}
          width={500}
          okText="อัปเดต"
          cancelText="ยกเลิก"
        >
          {stockProduct && (
            <>
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <strong>Stock ปัจจุบัน:</strong> {stockProduct.stock} {stockProduct.unit}
                  </Col>
                  <Col span={12}>
                    <strong>Stock ขั้นต่ำ:</strong> {stockProduct.min_stock} {stockProduct.unit}
                  </Col>
                </Row>
              </div>

              <Form
                form={stockForm}
                layout="vertical"
                initialValues={{
                  type: 'add'
                }}
              >
                <Form.Item
                  name="type"
                  label="ประเภทการอัปเดต"
                  rules={[{ required: true, message: 'กรุณาเลือกประเภท' }]}
                >
                  <Select>
                    <Option value="add">เพิ่ม Stock</Option>
                    <Option value="subtract">ลด Stock</Option>
                    <Option value="set">กำหนด Stock ใหม่</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="quantity"
                  label="จำนวน"
                  rules={[
                    { required: true, message: 'กรุณาใส่จำนวน' },
                    { type: 'number', min: 0, message: 'จำนวนต้องเป็นตัวเลขบวก' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="จำนวน"
                    addonAfter={stockProduct.unit}
                  />
                </Form.Item>

                <Form.Item
                  name="reason"
                  label="เหตุผล (ไม่บังคับ)"
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="เหตุผลในการอัปเดต stock..."
                  />
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>
      )}
    </div>
  )
}

export default ProductsPage
